import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Box, Container, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import LogoMark from "../LogoMark";
import AuthActions from "./AuthActions";
import NavMenuDesktop from "./NavMenuDesktop";
import NavMenuMobile from "./NavMenuMobile";
import CartIconButton from "./CartIconButton";
import MobileHeader from "./MobileHeader";
import { useNavigate } from "react-router-dom";
import AnimatedGreeting from "./AnimatedGreeting";
import { useNavbarContext } from "../../context/NavbarContext"; // ✨ new import

const Navbar = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [showNavbar, setShowNavbar] = useState(true); // ✨ manage dynamic hiding
  const [lastScrollY, setLastScrollY] = useState(0);
  const { scrollControlledNavbar } = useNavbarContext(); // ✨ context control

  useEffect(() => {
    if (!scrollControlledNavbar) {
      // Default scroll behavior (just shadow on scroll)
      const handleScrollDefault = () => {
        setIsScrolled(window.scrollY > 10);
      };
      window.addEventListener("scroll", handleScrollDefault);
      return () => window.removeEventListener("scroll", handleScrollDefault);
    } else {
      // Specialty Cakes scroll behavior (hide/show)
      const handleScrollSpecialty = () => {
        if (window.scrollY < lastScrollY) {
          setShowNavbar(true); // Scrolling up
        } else {
          setShowNavbar(false); // Scrolling down
        }
        setLastScrollY(window.scrollY);
      };
      window.addEventListener("scroll", handleScrollSpecialty);
      return () => window.removeEventListener("scroll", handleScrollSpecialty);
    }
  }, [scrollControlledNavbar, lastScrollY]);

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleCartClick = () => {
    navigate("/cart");
  };

  return (
    <AppBar
      position="sticky"
      elevation={isScrolled ? 1 : 0}
      sx={{
        backgroundColor: isScrolled ? "#fff" : "transparent",
        transition: "background-color 0.3s ease, top 0.3s ease",
        top: showNavbar ? 0 : "-135px", // ✨ move navbar off screen when hiding
      }}>
      {/* Top row: full-width greeting */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#FFDDD9",
          borderBottom: "1px solid #F4F4F4",
        }}>
        <Box
          sx={{
            maxWidth: "1440px",
            mx: "auto",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <AnimatedGreeting name={user?.firstName || "friend"} />
        </Box>
      </Box>

      {/* Main navbar content wrapped in container */}
      {isMobile ? (
        <>
          <MobileHeader
            onOpenDrawer={() => setMobileDrawerOpen(true)}
            onCartClick={handleCartClick}
            onLoginClick={handleLoginClick}
          />
          <NavMenuMobile
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
          />
        </>
      ) : (
        <Toolbar
          disableGutters
          sx={{
            width: "100%", // ✨ stretch toolbar background full width
            backgroundColor: "white",
            minHeight: { xs: 64, md: 80 }, // ✨ optional height control
            display: "flex",
            justifyContent: "center", // ✨ center the Container
          }}>
          <Container
            maxWidth="xl"
            disableGutters
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
            }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="CakeyHand Logo"
              sx={{
                height: 50, // or adjust depending on your logo proportions
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
              loading="lazy"
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
              <NavMenuDesktop />
              <AuthActions />
              <CartIconButton onClick={handleCartClick} />
            </Box>
          </Container>
        </Toolbar>
      )}
    </AppBar>
  );
};

export default Navbar;
